import React, { useState, useEffect, useContext } from 'react';
import classNames from 'classnames';
import CrossIcon from 'jl-design-system/elements/icons-jb/Close24PxOutlined';
import ChevronDownIcon from 'jl-design-system/elements/icons-jb/ChevronDown24PxOutlined';
import NotificationsIcon from 'jl-design-system/elements/icons-jb/Notifications24PxOutlined';
import parseCookies from '../../../lib/cookie/parser';
import styles from './delivery-threshold.scss';
import { UserContext } from '../../scaffold/UserContext';
import EnvVarsFeaturesContext from '../../scaffold/EnvVarsFeaturesContext';

const DeliveryThreshold = ({ hidden }) => {
  const deliveryThreshold = 50;
  const clickCollectThreshold = 30;

  const [dqCookie, setDeliveryCookie] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [displayMessages, toggleDisplayMessages] = useState(false);

  const [activeMessageIndex, updateActiveMessageIndex] = useState(0);
  const [delivery, updateDelivery] = useState({
    remainder: deliveryThreshold,
    qualifies: true,
  });

  const [clickCollect, updateClickCollect] = useState({
    remainder: clickCollectThreshold,
    qualifies: true,
  });

  const [notificationState, setNotificationState] = useState({
    minimised: false,
    hasNotification: false,
  });

  const {
    userState: {
      user: {
        signedIn,
        basket: { items: basketItems },
      },
    },
  } = useContext(UserContext);

  const {
    envVars: { basketApiUrl, basketApiClientId },
  } = useContext(EnvVarsFeaturesContext);

  const [prevBasketItems, updatePrevBasketItems] = useState(0);

  const writeNotificationCookie = ({
    minimised = false,
    hasNotification = false,
  }) => {
    window.document.cookie = `DQ-State=${JSON.stringify({
      minimised,
      hasNotification,
    })}; path=/;`;
  };

  const readNotificationCookie = () => {
    const dqState = parseCookies()['DQ-State'];
    if (dqState) {
      const { minimised, hasNotification } = JSON.parse(dqState);
      return {
        minimised,
        hasNotification,
      };
    }
    return { minimised: false, hasNotification: false };
  };

  const updateNotifcationState = state => {
    setNotificationState(state);
    writeNotificationCookie(state);
  };

  const updateDeliveryCookie = () => {
    const dq = parseCookies()['DQ-Info'];
    if (dq) {
      setDeliveryCookie(dq);
    }
  };

  const onBasketUpdated = () => {
    if (notificationState.minimised) {
      updateNotifcationState({ minimised: true, hasNotification: true });
    }
  };

  const toggle = () => {
    toggleDisplayMessages(!displayMessages);
    if (!notificationState.minimised) {
      setIsLoading(false);
    }
    updateNotifcationState({
      minimised: !notificationState.minimised,
      hasNotification: false,
    });
  };

  const isLargeScreen = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('only screen and (min-width: 1280px)').matches;

  const rootElement =
    typeof window !== 'undefined' && window.document?.documentElement;
  if (rootElement) {
    rootElement.style.setProperty('--jl-header-height', '156px');
  }

  useEffect(() => {
    if (dqCookie) {
      const { ukd, cc } = JSON.parse(dqCookie, (_, v) => {
        if (typeof v === 'number' && v % 1 === 0.5) return v.toFixed(2);
        return v;
      });

      if (basketItems > 0 && (ukd || ukd === 0 || cc || cc === 0)) {
        if ((ukd || ukd === 0) && ukd <= deliveryThreshold) {
          updateDelivery({ remainder: ukd, qualifies: true });
        } else {
          updateDelivery({ remainder: -1, qualifies: false });
        }

        if ((cc || cc === 0) && cc <= clickCollectThreshold) {
          updateClickCollect({ remainder: cc, qualifies: true });
        } else {
          updateClickCollect({ remainder: -1, qualifies: false });
        }
      } else {
        updateDelivery({ remainder: deliveryThreshold, qualifies: true });
        updateClickCollect({
          remainder: clickCollectThreshold,
          qualifies: true,
        });
      }
    }

    let timeoutId;
    if (isLoading) {
      if (isLargeScreen()) {
        timeoutId = setTimeout(() => {
          setIsLoading(false);
        }, 500);
      } else {
        setIsLoading(false);
      }
    }

    return () => clearTimeout(timeoutId);
  }, [dqCookie, basketItems, isLoading]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!isLargeScreen()) {
        if (delivery.qualifies && clickCollect.qualifies) {
          updateActiveMessageIndex(activeMessageIndex === 1 ? 0 : 1);
        } else {
          updateActiveMessageIndex(0);
          clearInterval(intervalId);
        }
      } else {
        clearInterval(intervalId);
      }
    }, 3500);

    return () => clearInterval(intervalId);
  }, [delivery, clickCollect, activeMessageIndex]);

  useEffect(() => {
    window.addEventListener(
      'jl-events.browser.basketQuantityUpdated',
      onBasketUpdated,
      false
    );

    return () => {
      window.removeEventListener(
        'jl-events.browser.basketQuantityUpdated',
        onBasketUpdated,
        false
      );
    };
  });

  useEffect(() => {
    const deliveryCookie = parseCookies()['DQ-Info'];
    if (!deliveryCookie) {
      const domain = `domain=${window.location.hostname.replace('www', '')}`;
      window.document.cookie = `DQ-Info={}; ${domain}; path=/;`;
    }
  }, []);

  useEffect(() => {
    const notificationCookieState = readNotificationCookie();
    setNotificationState(notificationCookieState);
    if (notificationCookieState.minimised) {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (signedIn) {
      const fetchBasket = async () => {
        try {
          const result = await fetch(basketApiUrl, {
            credentials: 'include',
            headers: { 'x-api-client-id': basketApiClientId },
          });

          if (!result.ok) {
            throw new Error(`Error fetching basket : ${result.status}`);
          }

          const basket = result.json();
          if (basket) {
            updateDeliveryCookie();
          }
        } catch (err) {
          console.log(err.message);
          updateDeliveryCookie();
        }
      };

      if (basketApiUrl && basketApiClientId) fetchBasket();
    }
  }, [signedIn]);

  useEffect(() => {
    if (basketItems >= 0) {
      if (basketItems !== prevBasketItems) {
        updatePrevBasketItems(basketItems);
      }

      if (
        basketItems > 0 ||
        (basketItems === 0 && prevBasketItems > 0) ||
        (basketItems === 0 && !signedIn)
      ) {
        updateDeliveryCookie();
      }
    }
  }, [basketItems]);

  if (!dqCookie) {
    return (
      <div
        data-testid="header-delivery-threshold"
        className={styles.deliveryThreshold__empty}
      />
    );
  }

  return (
    <>
      {!notificationState.minimised && (
        <div
          data-testid="header-delivery-threshold"
          className={classNames(styles.deliveryThreshold, {
            [styles.deliveryThreshold__hidden]: hidden,
            [styles.deliveryThreshold__singleMessage]:
              !delivery.qualifies || !clickCollect.qualifies,
          })}
        >
          {isLoading && (
            <div
              data-testid="header-threshold-ghost-load"
              className={styles.ghostLoad}
            />
          )}
          {delivery.qualifies && (
            <div
              data-testid="header-delivery-threshold-delivery"
              className={classNames(styles.deliveryThreshold__message, {
                [styles.deliveryThreshold__message__hidden]:
                  activeMessageIndex !== 0,
              })}
            >
              {delivery.remainder > 0 && (
                <div>
                  Spend £{delivery.remainder}
                  {`${
                    delivery.remainder === deliveryThreshold ? '' : ' more'
                  }`}{' '}
                  for <span>Free UK Delivery</span>
                </div>
              )}
              {delivery.remainder === 0 && (
                <div>
                  Order qualifies for <span>Free UK Delivery!</span>
                </div>
              )}
            </div>
          )}
          {clickCollect.qualifies && (
            <div
              data-testid="header-delivery-threshold-clickcollect"
              className={classNames(
                styles.deliveryThreshold__message,
                styles.deliveryThreshold__secondMessage,
                {
                  [styles.deliveryThreshold__message__hidden]:
                    delivery.qualifies && activeMessageIndex === 0,
                }
              )}
            >
              {clickCollect.remainder > 0 && (
                <div>
                  Spend £{clickCollect.remainder}{' '}
                  {`${
                    clickCollect.remainder === clickCollectThreshold
                      ? ''
                      : 'more'
                  }`}{' '}
                  for <span>Free Click & Collect</span>
                </div>
              )}
              {clickCollect.remainder === 0 && (
                <div>
                  Order qualifies for <span>Free Click & Collect!</span>
                </div>
              )}
            </div>
          )}
          {delivery.qualifies && clickCollect.qualifies && (
            <div className={styles.deliveryThreshold__pagination}>
              <span
                className={classNames(styles.deliveryThreshold__dot, {
                  [styles.deliveryThreshold__dot__active]:
                    activeMessageIndex === 0,
                })}
              />
              <span
                className={classNames(styles.deliveryThreshold__dot, {
                  [styles.deliveryThreshold__dot__active]:
                    activeMessageIndex === 1,
                })}
              />
            </div>
          )}
          <div
            className={styles.deliveryThreshold__dismiss}
            onClick={toggle}
            onKeyDown={toggle}
            tabIndex={0}
            role="button"
          >
            <CrossIcon />
          </div>
        </div>
      )}
      {notificationState.minimised && (
        <div
          className={classNames(styles.deliveryThreshold__toggle, {
            [styles.deliveryThreshold__hidden]: hidden,
          })}
          onClick={toggle}
          onKeyDown={toggle}
          tabIndex={0}
          role="button"
        >
          <div>
            {notificationState.hasNotification && (
              <NotificationsIcon iconState="icon-invert" />
            )}
          </div>
          <span>Want Free Delivery?</span>
          <div>
            <ChevronDownIcon iconState="icon-invert" />
          </div>
        </div>
      )}
    </>
  );
};

export default DeliveryThreshold;
